import {
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as d3 from 'd3';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CostCategory } from 'services/cost-breakdown-calculator';

const useStyles = makeStyles((theme: any) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[1],
  },
  cardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    letterSpacing: '0.5px',
  },
  chartsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(2),
    },
  },
  pieContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  pieSvgWrapper: {
    width: '100%',
    minHeight: '300px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pieSvg: {
    maxWidth: '100%',
    height: 'auto',
  },
  pieTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  legendContainer: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.75),
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  legendColor: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  legendText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  legendAmount: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginRight: theme.spacing(0.5),
  },
  legendPercentage: {
    color: theme.palette.text.secondary,
    fontSize: '11px',
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    color: theme.palette.text.secondary,
    fontSize: '14px',
  },
}));

interface CostBreakdownPieChartsProps {
  buyerCosts: CostCategory[];
  sellerCosts: CostCategory[];
}

/**
 * Format cents to currency string
 */
function formatCurrency(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(dollars);
}

/**
 * Get color for category based on theme palette
 */
function getCategoryColor(categoryKey: string, theme: any): string {
  const colorMap: Record<string, string> = {
    earnest: theme.palette.primary.main,
    commission: theme.palette.secondary.main,
    title: theme.palette.success.main,
    escrow: theme.palette.warning.main,
    taxes: theme.palette.error.main,
    other: theme.palette.info.main,
  };
  return colorMap[categoryKey] || theme.palette.info.main;
}

/**
 * Individual Pie Chart Component
 */
interface PieChartProps {
  title: string;
  data: CostCategory[];
  colors: Record<string, string>;
  svgRef: React.RefObject<HTMLDivElement>;
}

const PieChart: React.FC<PieChartProps> = ({
  title,
  data,
  colors,
  svgRef,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('closingDisclosure');

  // Calculate total
  const total = data.reduce((sum, item) => sum + item.amount, 0);

  useEffect(() => {
    if (!svgRef.current || !data || data.length === 0) {
      return;
    }

    // Clear previous content
    while (svgRef.current.firstChild) {
      svgRef.current.removeChild(svgRef.current.firstChild);
    }

    const containerWidth = svgRef.current.clientWidth || 400;
    const width = Math.min(containerWidth, 300);
    const height = 300;
    const radius = Math.min(width, height) / 2 - 40;

    // Create SVG
    const svg = d3
      .select(svgRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('class', classes.pieSvg);

    // Create main group
    const g = svg
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    // Create pie generator
    const pie = d3.pie<CostCategory>().value((d) => d.amount);

    // Create arc generator
    const arc = d3
      .arc<d3.PieArcDatum<CostCategory>>()
      .innerRadius(radius * 0.6) // Donut hole
      .outerRadius(radius);

    // Create arcs
    const arcs = g
      .selectAll('path')
      .data(pie(data))
      .enter()
      .append('path')
      .attr('d', arc)
      .attr('fill', (d) => colors[d.data.category_key] || colors.other)
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer');

    // Add tooltips on hover
    arcs.append('title').text(
      (d) => `${d.data.label}
${formatCurrency(d.data.amount)}
${((d.data.amount / total) * 100).toFixed(1)}%`,
    );

    // Add center text (total)
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '.3em')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .style('fill', '#666')
      .text(t('chart-total'));

    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '1.5em')
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text(formatCurrency(total));
  }, [data, colors, svgRef, classes.pieSvg, t, total]);

  return (
    <Box className={classes.pieContainer}>
      <Typography className={classes.pieTitle}>{title}</Typography>
      <div className={classes.pieSvgWrapper} ref={svgRef} />
    </Box>
  );
};

/**
 * Legend Component
 */
interface LegendProps {
  data: CostCategory[];
  colors: Record<string, string>;
}

const Legend: React.FC<LegendProps> = ({ data, colors }) => {
  const classes = useStyles();
  const { t } = useTranslation('closingDisclosure');

  const total = data.reduce((sum, item) => sum + item.amount, 0);

  return (
    <Box className={classes.legendContainer}>
      {data.map((item) => {
        const percentage = total > 0 ? ((item.amount / total) * 100).toFixed(1) : '0.0';
        return (
          <Box key={item.code} className={classes.legendItem}>
            <Box
              className={classes.legendColor}
              style={{ backgroundColor: colors[item.category_key] || colors.other }}
            />
            <Box className={classes.legendText}>
              <span className={classes.legendAmount}>{item.label}</span>
            </Box>
            <Box style={{ whiteSpace: 'nowrap', marginLeft: 'auto' }}>
              <span className={classes.legendAmount}>{formatCurrency(item.amount)}</span>
              <span className={classes.legendPercentage}>
                (
                {percentage}
                %)
              </span>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * CostBreakdownPieCharts - D3-based pie charts for buyer and seller costs
 *
 * Displays two donut charts side-by-side (responsive: stacked on mobile)
 * showing cost breakdown by category with legend and tooltips.
 *
 * Features:
 * - Two pie charts (Buyer left, Seller right)
 * - Color-coded by category (consistent with stacked bar chart)
 * - Center donut hole with total amount
 * - Legend with category, amount, percentage
 * - Responsive design (side-by-side desktop, stacked mobile)
 * - Tooltip on wedge hover
 * - Bilingual labels via i18n
 */
const CostBreakdownPieCharts: React.FC<CostBreakdownPieChartsProps> = ({
  buyerCosts,
  sellerCosts,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation('closingDisclosure');

  // Refs for pie charts
  const buyerSvgRef = useRef<HTMLDivElement>(null);
  const sellerSvgRef = useRef<HTMLDivElement>(null);

  // Build color map
  const colorMap: Record<string, string> = {
    earnest: getCategoryColor('earnest', theme),
    commission: getCategoryColor('commission', theme),
    title: getCategoryColor('title', theme),
    escrow: getCategoryColor('escrow', theme),
    taxes: getCategoryColor('taxes', theme),
    other: getCategoryColor('other', theme),
  };

  // Filter out zero-amount categories
  const filteredBuyerCosts = buyerCosts.filter((c) => c.amount > 0);
  const filteredSellerCosts = sellerCosts.filter((c) => c.amount !== 0);

  const hasBuyerCosts = filteredBuyerCosts.length > 0;
  const hasSellerCosts = filteredSellerCosts.length > 0;

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Typography className={classes.sectionTitle}>
          {t('cost-breakdown-title')}
        </Typography>

        {!hasBuyerCosts && !hasSellerCosts ? (
          <Box className={classes.emptyState}>{t('ui-error-empty-charges')}</Box>
        ) : (
          <Box className={classes.chartsContainer}>
            {/* Buyer Pie Chart */}
            <Box>
              {hasBuyerCosts ? (
                <>
                  <PieChart
                    title={t('cost-breakdown-pie-buyer')}
                    data={filteredBuyerCosts}
                    colors={colorMap}
                    svgRef={buyerSvgRef}
                  />
                  <Legend data={filteredBuyerCosts} colors={colorMap} />
                </>
              ) : (
                <Box className={classes.emptyState}>
                  {t('ui-error-empty-charges')}
                </Box>
              )}
            </Box>

            {/* Seller Pie Chart */}
            <Box>
              {hasSellerCosts ? (
                <>
                  <PieChart
                    title={t('cost-breakdown-pie-seller')}
                    data={filteredSellerCosts}
                    colors={colorMap}
                    svgRef={sellerSvgRef}
                  />
                  <Legend data={filteredSellerCosts} colors={colorMap} />
                </>
              ) : (
                <Box className={classes.emptyState}>
                  {t('ui-error-empty-charges')}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CostBreakdownPieCharts;
